import { useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { Plus, MoreHorizontal } from 'lucide-react';
import { useTasks } from '@/composables/crm/useTasks';
import { useDebounce } from '@/composables/shared/useDebounce';
import { PageContainer } from '@/components/layout/PageContainer';
import { DataTable } from '@/components/data-table/DataTable';
import { DataTableToolbar } from '@/components/data-table/DataTableToolbar';
import { DataTableColumnHeader } from '@/components/data-table/DataTableColumnHeader';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { StatusBadge } from '@/components/StatusBadge';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { formatDate } from '@/lib/formatters';
import { useState, useMemo } from 'react';
import type { Task } from '@/types/table';

export function TaskListPage() {
  const navigate = useNavigate();
  const { tasks, meta, isLoading, filters, completeTask, deleteTask } = useTasks();
  const [search, setSearch] = useState(filters.current.search);
  const debouncedSearch = useDebounce(search, 300);
  if (debouncedSearch !== filters.current.search) filters.set({ search: debouncedSearch });

  const [deleteTarget, setDeleteTarget] = useState<Task | null>(null);

  const columns: ColumnDef<Task, unknown>[] = useMemo(() => [
    { accessorKey: 'title', header: ({ column }) => <DataTableColumnHeader column={column} title="Title" />, cell: ({ row }) => <span className="font-medium">{row.original.title}</span> },
    { accessorKey: 'status', header: 'Status', cell: ({ row }) => <StatusBadge status={row.original.status} type="task" /> },
    { accessorKey: 'priority', header: 'Priority', cell: ({ row }) => <StatusBadge status={row.original.priority} type="priority" /> },
    { accessorKey: 'dueDate', header: ({ column }) => <DataTableColumnHeader column={column} title="Due Date" />, cell: ({ row }) => formatDate(row.original.dueDate) },
    { accessorKey: 'assignedTo', header: 'Assignee', cell: ({ row }) => row.original.assignedTo ? `${row.original.assignedTo.firstName} ${row.original.assignedTo.lastName}` : '-' },
    {
      id: 'actions',
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => e.stopPropagation()}>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/tasks/${row.original.id}`); }}>View</DropdownMenuItem>
            {row.original.status !== 'DONE' && (
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); completeTask.mutate(row.original.id); }}>Complete</DropdownMenuItem>
            )}
            <DropdownMenuItem className="text-destructive" onClick={(e) => { e.stopPropagation(); setDeleteTarget(row.original); }}>Delete</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  return (
    <PageContainer title="Tasks" description="Manage tasks across your CRM" actions={<Button onClick={() => navigate('/tasks/new')}><Plus className="mr-2 h-4 w-4" />New Task</Button>}>
      <DataTableToolbar searchValue={search} onSearchChange={setSearch} searchPlaceholder="Search tasks..." />
      <DataTable columns={columns} data={tasks} isLoading={isLoading} page={meta?.page ?? 1} totalPages={meta?.totalPages ?? 1} total={meta?.total ?? 0} pageSize={filters.current.limit} onPageChange={(p) => filters.set({ page: p })} onPageSizeChange={(s) => filters.set({ limit: s })} onRowClick={(row) => navigate(`/tasks/${row.id}`)} />
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={() => setDeleteTarget(null)}
        title="Delete Task"
        description={`Are you sure you want to delete "${deleteTarget?.title}"? This action cannot be undone.`}
        variant="destructive"
        confirmLabel="Delete"
        onConfirm={() => { if (deleteTarget) { deleteTask.mutate(deleteTarget.id); setDeleteTarget(null); } }}
        isLoading={deleteTask.isPending}
      />
    </PageContainer>
  );
}
